import React from "react";
import ButtonSpinner from "../controls/ButtonSpinner";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "ghost"
  | "soft";

export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const baseClasses =
  "inline-flex items-center justify-center gap-2 rounded-xl border font-semibold shadow-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "border-[#16A34A] bg-[#16A34A] text-white hover:border-[#15803D] hover:bg-[#15803D] focus-visible:outline-[#16A34A]",
  secondary:
    "border-[#E2E8F0] bg-white text-[#0F172A] hover:border-[#16A34A] hover:bg-[#F0FDF4] hover:text-[#15803D] focus-visible:outline-[#16A34A]",
  success:
    "border-[#16A34A] bg-[#16A34A] text-white hover:border-[#15803D] hover:bg-[#15803D] focus-visible:outline-[#16A34A]",
  danger:
    "border-[#FECACA] bg-white text-[#DC2626] hover:border-[#FCA5A5] hover:bg-[#FEF2F2] focus-visible:outline-[#DC2626]",
  ghost:
    "border-transparent bg-transparent text-[#64748B] shadow-none hover:bg-[#F6F8FB] hover:text-[#0F172A] focus-visible:outline-[#16A34A]",
  soft: "border-[#BBF7D0] bg-[#F0FDF4] text-[#15803D] hover:border-[#86EFAC] hover:bg-[#DCFCE7] focus-visible:outline-[#16A34A]",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "min-h-9 px-3 py-1.5 text-sm",
  md: "min-h-10 px-3.5 py-2 text-sm",
  lg: "min-h-11 px-4 py-2.5 text-base",
};

export default function Button({
  variant = "secondary",
  size = "sm",
  loading = false,
  disabled = false,
  className = "",
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`.trim()}
      disabled={loading || disabled}
      {...props}
    >
      <ButtonSpinner spin={loading} text={children} />
    </button>
  );
}
